import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Checkbox,
  CloudImage,
  Container,
  Form,
  InnerForm,
  Modal,
  PremiumFeature,
  Span,
  Text,
  useModal,
} from "@fluidattacks/design";
import capitalize from "lodash/capitalize";
import { Fragment, useCallback, useState } from "react";
import * as React from "react";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { array, object } from "yup";

import type { IGroup } from "./queries";
import {
  GET_USER_ORGANIZATIONS_GROUPS,
  REQUEST_GROUPS_UPGRADE_MUTATION,
} from "./queries";
import { UpgradeSuccessModal } from "./upgrade-success-modal";

import { useAudit } from "hooks/use-audit";
import { Logger } from "utils/logger";

interface IUpgradeGroupsModalProps {
  readonly onClose: () => void;
  readonly isRestrictedReattack?: boolean;
  readonly title?: string;
  readonly img?: string;
  readonly description?: string;
}

const UpgradeGroupsModal: React.FC<IUpgradeGroupsModalProps> = ({
  onClose,
  isRestrictedReattack = false,
  title = "components.sideBar.help.expert.trial.title",
  img = "integrates/plans/talk-to-a-pentester",
  description = "components.sideBar.help.expert.trial.description",
}): JSX.Element => {
  const { t } = useTranslation();
  const modalProps = useModal("upgrade-groups-modal");
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const { groupName: groupDisplayName } = useParams() as {
    groupName: string;
  };
  const handleToggleSuccessModal = useCallback((): void => {
    setSuccessModalOpen((prevState): boolean => !prevState);
  }, []);

  const { data } = useQuery(GET_USER_ORGANIZATIONS_GROUPS, {
    fetchPolicy: "cache-first",
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        Logger.warning("Couldn't load user organizations groups", error);
      });
    },
    skip: isRestrictedReattack,
  });
  const organizations = data?.me.organizations ?? [];
  const groups = organizations.reduce<IGroup[]>(
    (previousValue, currentValue): IGroup[] => [
      ...previousValue,
      ...(currentValue.groups as IGroup[]),
    ],
    [],
  );
  const upgradableGroups = groups
    .filter(
      (group): boolean =>
        !group.serviceAttributes.includes("has_advanced") &&
        group.permissions.includes("request_group_upgrade"),
    )
    .map((group): string => group.name);
  const canUpgrade = upgradableGroups.length > 0;

  const { addAuditEvent } = useAudit();
  const [requestGroupsUpgrade, { loading }] = useMutation(
    REQUEST_GROUPS_UPGRADE_MUTATION,
    {
      onCompleted: (): void => {
        handleToggleSuccessModal();
        addAuditEvent("Group.Upgrade", groupDisplayName);
      },
      onError: ({ graphQLErrors }): void => {
        graphQLErrors.forEach((error): void => {
          Logger.error("Couldn't request groups upgrade", error);
        });
      },
    },
  );

  const handleSubmit = useCallback(
    async (values: { groupNames: string[] }): Promise<void> => {
      await requestGroupsUpgrade({
        variables: {
          groupNames: isRestrictedReattack
            ? [groupDisplayName]
            : values.groupNames,
        },
      });
    },
    [requestGroupsUpgrade, groupDisplayName, isRestrictedReattack],
  );

  const validationSchema = object().shape({
    groupNames: array().min(1, t("validations.someRequired")),
  });

  return (
    <Fragment>
      <Modal
        modalRef={{ ...modalProps, close: onClose, isOpen: true }}
        size={"sm"}
        title={
          <Container
            alignItems={"center"}
            display={"flex"}
            justify={"space-between"}
            width={"100%"}
          >
            <Text color={"black"} fontWeight={"bold"} size={"lg"}>
              {t(title)}
            </Text>
            <PremiumFeature margin={1} text={"Upgrade"} />
          </Container>
        }
      >
        <Container>
          <Container>
            <CloudImage alt={img} publicId={img} width={"100%"} />
          </Container>
          <Container pt={1} px={1.5}>
            <Trans
              components={{
                bold: (
                  <Span fontWeight={"bold"} size={"md"}>
                    {undefined}
                  </Span>
                ),
              }}
              i18nKey={description}
              t={t}
            />
          </Container>
          <Form
            defaultDisabled={false}
            defaultValues={{ groupNames: upgradableGroups }}
            onSubmit={handleSubmit}
            showButtons={false}
            yupSchema={isRestrictedReattack ? undefined : validationSchema}
          >
            <InnerForm>
              {({ register }): JSX.Element => (
                <Fragment>
                  {!isRestrictedReattack && canUpgrade && (
                    <Fragment>
                      <Text mb={1} mt={1} size={"md"}>
                        {t("upgrade.select")}
                      </Text>
                      <Container
                        display={"flex"}
                        flexDirection={"column"}
                        gap={1}
                        height={"100px"}
                        scroll={"y"}
                      >
                        {upgradableGroups.map(
                          (groupName): JSX.Element => (
                            <Checkbox
                              {...register("groupNames")}
                              defaultChecked={true}
                              key={groupName}
                              label={capitalize(groupName)}
                              name={"groupNames"}
                              value={groupName}
                            />
                          ),
                        )}
                      </Container>
                    </Fragment>
                  )}
                  {!isRestrictedReattack && !canUpgrade && (
                    <Text size={"md"}>{t("upgrade.unauthorized")}</Text>
                  )}
                  <Container
                    display={"flex"}
                    gap={0.5}
                    pt={0.75}
                    width={"100%"}
                  >
                    <Button
                      onClick={onClose}
                      type={"button"}
                      variant={"tertiary"}
                      width={"100%"}
                    >
                      {t("buttons.cancel")}
                    </Button>
                    <Button disabled={loading} type={"submit"} width={"100%"}>
                      {t("buttons.upgrade")}
                    </Button>
                  </Container>
                </Fragment>
              )}
            </InnerForm>
          </Form>
        </Container>
      </Modal>
      {successModalOpen && <UpgradeSuccessModal onClose={onClose} />}
    </Fragment>
  );
};

export { UpgradeGroupsModal };
